import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon, 
  DocumentTextIcon, 
  MagnifyingGlassIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { AIRelatedDocument } from '../types/DocumentTypes';

interface AIRelatedDocumentsProps {
  currentDocument: {
    title: string;
    description?: string;
    clientId?: string;
    matterId?: string;
    tags?: string[];
  };
  selectedDocuments: string[];
  onDocumentsSuggested: (documents: string[]) => void;
  onDocumentAdd: (documentId: string) => void;
  disabled?: boolean;
}

export const AIRelatedDocuments: React.FC<AIRelatedDocumentsProps> = ({
  currentDocument,
  selectedDocuments,
  onDocumentsSuggested,
  onDocumentAdd,
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<AIRelatedDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Generate AI suggestions when document metadata changes
  useEffect(() => {
    if (currentDocument.title.trim() && !disabled) {
      generateSuggestions();
    }
  }, [currentDocument.title, currentDocument.description, currentDocument.clientId, currentDocument.matterId]);

  const generateSuggestions = async () => {
    if (!currentDocument.title.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call to semantic search
      // const response = await fetch('/api/v1/dms/documents/search', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     q: currentDocument.title,
      //     type: 'semantic',
      //     client_id: currentDocument.clientId,
      //     matter_id: currentDocument.matterId,
      //     limit: 10
      //   })
      // });
      // const data = await response.json();

      // Mock AI suggestions for now
      const mockSuggestions: AIRelatedDocument[] = generateMockSuggestions(currentDocument);
      
      // Filter out already selected documents
      const filteredSuggestions = mockSuggestions.filter(
        suggestion => !selectedDocuments.includes(suggestion.documentId)
      );

      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } catch (err) {
      setError('Failed to generate AI suggestions');
      console.error('AI related documents error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockSuggestions = (document: any): AIRelatedDocument[] => {
    const suggestions: AIRelatedDocument[] = [];
    const titleLower = document.title.toLowerCase();

    // Same matter suggestions
    if (document.matterId) {
      suggestions.push(
        {
          documentId: 'doc-001',
          title: 'Contract Amendment - Smith v. Jones',
          relevanceScore: 0.95,
          relationshipType: 'same_matter',
          reasoning: 'Same matter: Smith v. Jones case'
        },
        {
          documentId: 'doc-002',
          title: 'Discovery Response - Smith v. Jones',
          relevanceScore: 0.88,
          relationshipType: 'same_matter',
          reasoning: 'Same matter: Smith v. Jones case'
        }
      );
    }

    // Same client suggestions
    if (document.clientId) {
      suggestions.push(
        {
          documentId: 'doc-003',
          title: 'Client Agreement - John Smith',
          relevanceScore: 0.82,
          relationshipType: 'same_client',
          reasoning: 'Same client: John Smith'
        }
      );
    }

    // Similar content suggestions based on title
    if (titleLower.includes('contract') || titleLower.includes('agreement')) {
      suggestions.push(
        {
          documentId: 'doc-004',
          title: 'Service Agreement Template',
          relevanceScore: 0.78,
          relationshipType: 'similar_content',
          reasoning: 'Similar document type: Contract/Agreement'
        },
        {
          documentId: 'doc-005',
          title: 'Employment Contract - Final',
          relevanceScore: 0.75,
          relationshipType: 'similar_content',
          reasoning: 'Similar document type: Contract/Agreement'
        }
      );
    }

    if (titleLower.includes('brief') || titleLower.includes('motion')) {
      suggestions.push(
        {
          documentId: 'doc-006',
          title: 'Motion for Summary Judgment',
          relevanceScore: 0.85,
          relationshipType: 'similar_content',
          reasoning: 'Similar document type: Legal Brief/Motion'
        },
        {
          documentId: 'doc-007',
          title: 'Appellate Brief - Smith Case',
          relevanceScore: 0.80,
          relationshipType: 'similar_content',
          reasoning: 'Similar document type: Legal Brief/Motion'
        }
      );
    }

    // Legal precedent suggestions
    suggestions.push(
      {
        documentId: 'doc-008',
        title: 'Precedent Case Law - Contract Disputes',
        relevanceScore: 0.70,
        relationshipType: 'legal_precedent',
        reasoning: 'Relevant legal precedent for contract matters'
      }
    );

    return suggestions.sort((a, b) => b.relevanceScore - a.relevanceScore);
  };

  const handleSuggestionClick = (suggestion: AIRelatedDocument) => {
    onDocumentAdd(suggestion.documentId);
    // Remove the suggestion from the list
    setSuggestions(prev => prev.filter(s => s.documentId !== suggestion.documentId));
  };

  const handleAddAllSuggestions = () => {
    const newDocumentIds = suggestions.map(s => s.documentId);
    onDocumentsSuggested(newDocumentIds);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const getRelevanceColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 0.8) return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
    if (score >= 0.7) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getRelationshipColor = (type: string) => {
    switch (type) {
      case 'same_matter': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'same_client': return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
      case 'similar_content': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'legal_precedent': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRelationshipIcon = (type: string) => {
    switch (type) {
      case 'same_matter': return '📁';
      case 'same_client': return '👤';
      case 'similar_content': return '📄';
      case 'legal_precedent': return '⚖️';
      default: return '🔗';
    }
  };

  if (disabled) return null;

  return (
    <div className="space-y-3">
      {/* AI Suggestions Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            AI Related Documents
          </span>
          {loading && (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
          )}
        </div>
        
        {suggestions.length > 0 && (
          <button
            onClick={handleAddAllSuggestions}
            className="text-xs text-purple-600 hover:text-purple-700 font-medium"
          >
            Add All
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </div>
      )}

      {/* Suggestions List */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="space-y-2">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.documentId}
              className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {suggestion.title}
                    </p>
                    {suggestion.reasoning && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {suggestion.reasoning}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm">{getRelationshipIcon(suggestion.relationshipType)}</span>
                  <span className={`px-2 py-1 text-xs rounded-full border ${getRelationshipColor(suggestion.relationshipType)}`}>
                    {suggestion.relationshipType.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full border ${getRelevanceColor(suggestion.relevanceScore)}`}>
                    {Math.round(suggestion.relevanceScore * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-2">
                <CheckIcon className="h-4 w-4 text-green-600" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Suggestions Message */}
      {showSuggestions && suggestions.length === 0 && !loading && (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          <DocumentTextIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No related documents found</p>
          <p className="text-xs">Try adding more context or selecting a matter</p>
        </div>
      )}

      {/* Generate Suggestions Button */}
      {!showSuggestions && !loading && (
        <button
          onClick={generateSuggestions}
          disabled={!currentDocument.title.trim()}
          className="w-full px-3 py-2 text-sm text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          Find Related Documents
        </button>
      )}

      {/* Info Panel */}
      <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-3">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="h-4 w-4 text-lex-accent mt-0.5 flex-shrink-0" />
          <div className="text-xs text-lex-accent">
            <p className="font-medium mb-1">AI-powered document linking</p>
            <p>Uses semantic search to find related documents based on content, matter, client, and legal context.</p>
          </div>
        </div>
      </div>
    </div>
  );
}; 